import React, { useEffect, useState } from 'react';
import { listDiseaseCategories } from '../api/catalogs';
import { ApiError } from '../api/client';

export default function DiseaseSelector({ value, onChange, samples, rarity, usefulness }) {
  const [categories, setCategories] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    listDiseaseCategories()
      .then((r) => { if (!cancelled) setCategories(r.categories || []); })
      .catch((err) => { if (!cancelled) setError(err instanceof ApiError ? err.code : 'load_failed'); });
    return () => { cancelled = true; };
  }, []);

  const selected = categories?.find((c) => c.value === value);
  const isFiltered = selected && selected.value !== 'none';

  return (
    <div style={containerStyle}>
      <label style={labelStyle} htmlFor="disease-select">Disease filter (optional)</label>
      <div style={rowStyle}>
        <select
          id="disease-select"
          value={value || 'none'}
          onChange={(e) => onChange(e.target.value)}
          style={selectStyle}
          disabled={!categories}
        >
          {categories ? (
            categories.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))
          ) : (
            <option>Loading disease catalog…</option>
          )}
        </select>
        {isFiltered && (
          <button type="button" onClick={() => onChange('none')} style={clearBtnStyle}>
            Clear filter
          </button>
        )}
      </div>
      {error && <div style={errorStyle}>Couldn't load disease catalog ({error}). Scoring guidance is disabled.</div>}
      {isFiltered && selected.description && (
        <p style={descStyle}>{selected.description}</p>
      )}
      {isFiltered && selected.standard_cohort && (
        <div style={hintsStyle}>
          <span style={hintStyle}>
            Typical cohort: small {fmt(selected.standard_cohort.small)}, standard {fmt(selected.standard_cohort.standard)}, large {fmt(selected.standard_cohort.large)} patients.
          </span>
          {cohortWarning(samples, selected.standard_cohort, selected.label) && (
            <div style={warnStyle} data-testid="cohort-warning">
              {cohortWarning(samples, selected.standard_cohort, selected.label)}
            </div>
          )}
        </div>
      )}
      {isFiltered && selected.factor_ranges && (
        <FactorRangeHints
          ranges={selected.factor_ranges}
          rarity={rarity}
          usefulness={usefulness}
          label={selected.label}
        />
      )}
    </div>
  );
}

function fmt(n) { return Number(n).toLocaleString('en-US'); }

function FactorRangeHints({ ranges, rarity, usefulness, label }) {
  const rows = [];
  const r = Number(rarity);
  const u = Number(usefulness);
  if (ranges.rarity) {
    rows.push({
      name: 'Rarity',
      range: ranges.rarity,
      value: r,
      offBand: Number.isFinite(r) && (r < ranges.rarity.low || r > ranges.rarity.high),
    });
  }
  if (ranges.usefulness) {
    rows.push({
      name: 'Usefulness',
      range: ranges.usefulness,
      value: u,
      offBand: Number.isFinite(u) && (u < ranges.usefulness.low || u > ranges.usefulness.high),
    });
  }
  if (rows.length === 0) return null;
  return (
    <div style={rangeWrapStyle}>
      <div style={rangeHeadStyle}>Typical for {label}:</div>
      {rows.map((row) => (
        <div key={row.name} style={row.offBand ? rangeRowOffStyle : rangeRowStyle} data-testid={`range-${row.name.toLowerCase()}`}>
          <span>{row.name} {row.range.low}{row.range.low !== row.range.high ? `–${row.range.high}` : ''}</span>
          <span style={rangeValueStyle}>
            you: {Number.isFinite(row.value) ? row.value : '—'}
            {row.offBand && ' · off-band'}
          </span>
        </div>
      ))}
    </div>
  );
}

// cohortWarning returns a soft advisory string (not a hard block — V1
// scope deliberately avoids blocking the demo path) when the user's
// sample size is materially outside the disease's typical band.
function cohortWarning(samples, cohort, label) {
  const n = Number(samples);
  if (!Number.isFinite(n) || n <= 0 || !cohort) return null;
  const niceLabel = label || 'this disease';
  if (n < cohort.small) {
    return `Your cohort of ${fmt(n)} is below the minimum viable cohort (${fmt(cohort.small)}) for ${niceLabel}. Pricing may not reflect market norms.`;
  }
  if (n > cohort.large * 2) {
    return `Your cohort of ${fmt(n)} is much larger than typical for ${niceLabel} (large = ${fmt(cohort.large)}). Per-patient rates compress at this scale.`;
  }
  return null;
}

// Wrap the picker + description + hints in a tinted card so the
// disease-context block is visually grouped — per Sarah's feedback the
// updates were too subtle to notice. Light teal accent on a faint
// teal-bg ties it back to the brand palette without shouting.
const containerStyle = {
  marginBottom: '24px',
  padding: '14px 16px 16px',
  border: '1px solid var(--accent-bg2)',
  background: 'var(--accent-bg)',
  borderRadius: '6px',
};
const labelStyle = { display: 'block', fontSize: '12px', color: 'var(--accent-strong)', marginBottom: '8px', fontWeight: 500 };
const rowStyle = { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' };
const selectStyle = {
  height: '44px',
  // Extra right padding so the native dropdown arrow doesn't kiss the border.
  padding: '0 36px 0 14px',
  border: '1px solid var(--line)',
  borderRadius: '4px',
  background: 'var(--surface)',
  fontSize: '14px',
  fontFamily: 'var(--font)',
  color: 'var(--ink)',
  minWidth: '300px',
  cursor: 'pointer',
};
const clearBtnStyle = {
  height: '32px',
  padding: '0 14px',
  border: '1px solid var(--line)',
  borderRadius: '999px',
  background: 'var(--surface)',
  color: 'var(--ink2)',
  fontSize: '12.5px',
  cursor: 'pointer',
  fontFamily: 'var(--font)',
};
const descStyle = { margin: '12px 0 0', fontSize: '13px', color: 'var(--ink2)', lineHeight: 1.55, maxWidth: '620px' };
const hintsStyle = { marginTop: '8px' };
const hintStyle = { fontSize: '12px', color: 'var(--accent)' };
const warnStyle = {
  marginTop: '10px',
  padding: '8px 10px',
  background: 'var(--amber-bg)',
  color: 'var(--amber-strong)',
  borderRadius: '4px',
  fontSize: '12.5px',
  lineHeight: 1.5,
};
const errorStyle = { marginTop: '10px', fontSize: '12px', color: 'var(--warm)' };

const rangeWrapStyle = { marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '4px' };
const rangeHeadStyle = { fontSize: '11.5px', color: 'var(--accent-strong)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' };
const rangeRowBase = { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '4px 0', fontSize: '12.5px' };
const rangeRowStyle = { ...rangeRowBase, color: 'var(--ink2)' };
const rangeRowOffStyle = { ...rangeRowBase, color: 'var(--amber-strong)' };
const rangeValueStyle = { fontVariantNumeric: 'tabular-nums', fontSize: '12px' };
